import './styles.css'

import BulletList from '@tiptap/extension-bullet-list'
import CharacterCount from '@tiptap/extension-character-count'
import { Color } from '@tiptap/extension-color'
import Document from '@tiptap/extension-document'
import Heading from '@tiptap/extension-heading'
import Highlight from '@tiptap/extension-highlight'
import Link from '@tiptap/extension-link'
import ListItem from '@tiptap/extension-list-item'
import Paragraph from '@tiptap/extension-paragraph'
import Placeholder from '@tiptap/extension-placeholder'
import Text from '@tiptap/extension-text'
import TextAlign from '@tiptap/extension-text-align'
import TextStyle from '@tiptap/extension-text-style'
import Underline from '@tiptap/extension-underline'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import {
  ComponentProps,
  forwardRef,
  ReactNode,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import { useClickOutside } from '@/hooks/useClickOutside'
import cn from '@/utils/tailwind/cn'

import { Toolbar } from './components/TextEditorToolbar'
import { TextEditorContext } from './hooks/useTextEditorContext'

type ToolbarProps = Omit<
  ComponentProps<typeof Toolbar>,
  'editor' | 'isToolbarTopActive'
>

type Props = {
  allowClear?: boolean
  autoFocus?: boolean
  disabled?: boolean
  value?: string
  placeholder?: string
  onChange?: (value: string) => void
  onBlur?: (value: string) => void
  minHeight?: number
  maxHeight?: number
  hideToolbar?: boolean
  children?: ReactNode
  extraContent?: ReactNode
  /**
   * This make the editor always focused (border and toolbar)
   */
  forceFocused?: boolean
  minHeightExpanded?: number
  className?: string
  resize?: boolean
  inputId?: string
  maxLength?: number
} & ToolbarProps

export type TEditorForwardedProps = {
  getValue: () => string
}

const processVariables = (content: string) => {
  return content.replace(/\{\{([^}]+)\}\}/g, (_, variableName: string) => {
    return `<span data-variable="${variableName.trim()}" contenteditable="false" class="variable">{{${variableName.trim()}}}</span>`
  })
}

export const CustomTemplateEditor = forwardRef<TEditorForwardedProps, Props>(
  (
    {
      value,
      onChange,
      onBlur,
      autoFocus,
      disabled,
      placeholder,
      hideToolbar,
      inputId,
      getContainer,
      resize,
      children,
      forceFocused,
      maxHeight = 340,
      minHeight = 100,
      minHeightExpanded = minHeight + 44,
      maxLength,
      tools,
      className,
      extraContent,
    },
    editorRef
  ) => {
    const [isEditorFocused, setIsEditorFocused] = useState(false)
    const rootRef = useRef<HTMLDivElement | null>(null)

    const editor = useEditor({
      onCreate: ({ editor }) => {
        if (autoFocus) editor.commands.focus()
        const processedValue = value ? processVariables(value) : ''
        editor.commands.setContent(processedValue)
      },
      editable: !disabled,
      onFocus() {
        setIsEditorFocused(true)
      },
      onBlur: () => {
        if (onChange) {
          const editorValue = getTextValue()
          onChange(editorValue)
        }
        if (onBlur) {
          const editorValue = getTextValue()
          onBlur(editorValue)
        }
      },
      onUpdate: () => {
        if (onChange) {
          const editorValue = getTextValue()
          onChange(editorValue)
        }
      },
      content: value,
      extensions: [
        Color.configure({ types: [TextStyle.name, ListItem.name] }),
        TextStyle.configure(),
        Highlight.configure({ multicolor: true }),
        StarterKit.configure({
          heading: false,
          blockquote: false,
          codeBlock: false,
          code: false,
        }),
        BulletList.configure({
          keepMarks: false,
        }),
        CharacterCount.configure({
          limit: maxLength,
        }),
        ListItem,
        Link.configure({
          openOnClick: false,
          autolink: false,
          HTMLAttributes: {
            target: '_blank',
          },
          linkOnPaste: true,
        }),
        Placeholder.configure({
          placeholder: placeholder ?? 'Type something...',
        }),
        Underline,
        Document,
        Paragraph,
        Text,
        Heading,
        TextAlign.configure({
          types: ['heading', 'paragraph', 'image'],
        }),
      ],
    })

    const getTextValue = useCallback(() => {
      const textValue = editor?.getHTML() ?? ''
      const isEmpty =
        textValue.replace(/<\s*p\s*>|<\/\s*p\s*>/g, '').trim()?.length === 0

      if (isEmpty) {
        return ''
      }

      const cleanedHTML = textValue
        .replace(
          /<span[^>]*data-variable="[^"]*"[^>]*>({{.*?}})<\/span>/gi,
          '$1'
        )
        .trim()

      return cleanedHTML
    }, [editor])

    const isFocused = forceFocused || isEditorFocused || !editor?.isEmpty

    const isToolbarTopActive = (() => {
      if (hideToolbar) return false

      return isFocused
    })()

    useClickOutside(rootRef, () => setIsEditorFocused(false))
    useImperativeHandle(editorRef, () => ({ getValue: getTextValue }))

    return (
      <section
        className={cn(
          'border-ant-gray relative flex flex-col rounded-sm border border-solid transition-all duration-100',
          { 'pt-9': isToolbarTopActive, '!border-primary': isEditorFocused },
          className
        )}
        data-id="comment-editor"
        ref={rootRef}
        style={{
          minHeight: isFocused ? minHeightExpanded : minHeight,
          maxHeight,
        }}
      >
        <TextEditorContext.Provider
          value={{
            editor,
            isToolbarTopActive,
            isEditorFocused: isFocused,
            getTextValue,
          }}
        >
          {editor && (
            <Toolbar
              editor={editor}
              getContainer={getContainer}
              isToolbarTopActive={isToolbarTopActive}
              tools={tools}
            />
          )}

          {extraContent}

          <EditorContent
            className="flex grow overflow-y-auto break-words px-3 py-2"
            editor={editor}
            id={inputId}
            style={{ resize: resize ? 'vertical' : 'none' }}
          />
          {children}
        </TextEditorContext.Provider>
      </section>
    )
  }
)
CustomTemplateEditor.displayName = 'CustomTemplateEditor'
